import LoadingOverlayDots from "@/components/hlidaci-pes/LoadingLayoverDots";
import { WatchDogSelectorButton } from "@/components/hlidaci-pes/watchdog-page/WatchDogSelectorButton";
import {
  getSearchModeLabel,
  getWatchDogIcon,
} from "@/components/hlidaci-pes/watchdog-page/watchDogOverviewUtils";
import { HomeSelectionQuickDetails } from "@/components/index/fragments/HomeSelectionQuickDetails";
import { StatusPill } from "@/components/portfolio/StatusPill";
import { Button } from "@/components/ui/Button";
import { SummaryStatGrid } from "@/components/ui/SummaryGrid";
import { DemoBrushBadge } from "@/components/ui/DemoBrushBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { useLanguage } from "@/context/LanguageContext";
import type { EstateOffer, WatchDog } from "@/types";
import { getEstateValueLabel } from "@/utils/estateValueLabels";
import { PlusIcon } from "@heroicons/react/24/outline";
import { motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import type { ReactNode } from "react";

export type WatchDogSelectionPanelCardProps = {
  watchdogs: WatchDog[];
  watchDogCountLabel: string;
  isLoading: boolean;
  selectedWatchDogId: number | null;
  onSelectWatchDog: (watchDog: WatchDog) => void;
  tooltip: string;
  isMembershipLoading: boolean;
  onAddWatchDogClick: () => void;
  emptyContent?: ReactNode;
  footer?: ReactNode;
  latestOffers?: EstateOffer[];
  latestContent?: ReactNode;
  showSelectedDetails?: boolean;
  summaryOffers?: EstateOffer[];
  isSummaryLoading?: boolean;
  summaryResultCountOverride?: number;
  latestResultLabelOverride?: string;
  averagePriceChangeOverride?: number;
  isDemo?: boolean;
  headerAction?: ReactNode;
};

function parseOfferPrice(price: string) {
  const value = Number(price.replace(/[^\d]/g, ""));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function getLocalDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getOfferAgeInDays(createdAt: string) {
  const [year, month, day] = createdAt.slice(0, 10).split("-").map(Number);
  const today = new Date();
  const createdDateUtc = Date.UTC(year, month - 1, day);
  const todayUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return Math.floor((todayUtc - createdDateUtc) / (24 * 60 * 60 * 1000));
}

function getAverageOfferPrice(offers: EstateOffer[]) {
  const prices = offers
    .map((offer) => parseOfferPrice(offer.price))
    .filter((price): price is number => price != null);

  return prices.length
    ? Math.round(
        prices.reduce((total, price) => total + price, 0) / prices.length,
      )
    : null;
}

function normalizeDetailText(value: unknown) {
  if (value == null) return null;

  const normalized = String(value).trim();
  if (!normalized || ["null", "undefined"].includes(normalized.toLowerCase())) {
    return null;
  }

  return normalized;
}

function normalizeRangeValue(value: unknown) {
  const normalized = normalizeDetailText(value);
  if (normalized == null) return null;

  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : null;
}

function formatRange(
  from: unknown,
  to: unknown,
  suffix: string,
  locale: string,
) {
  const normalizedFrom = normalizeRangeValue(from);
  const normalizedTo = normalizeRangeValue(to);
  const formatValue = (value: number) =>
    `${value.toLocaleString(locale)}${suffix}`;

  if (normalizedFrom != null && normalizedTo != null) {
    return `${formatValue(normalizedFrom)} – ${formatValue(normalizedTo)}`;
  }
  if (normalizedFrom != null) return `≥ ${formatValue(normalizedFrom)}`;
  if (normalizedTo != null) return `≤ ${formatValue(normalizedTo)}`;
  return null;
}

export function WatchDogSelectionPanelCard({
  watchdogs,
  watchDogCountLabel,
  isLoading,
  selectedWatchDogId,
  onSelectWatchDog,
  tooltip,
  isMembershipLoading,
  onAddWatchDogClick,
  emptyContent,
  footer,
  latestOffers,
  latestContent,
  showSelectedDetails = false,
  summaryOffers,
  isSummaryLoading = false,
  summaryResultCountOverride,
  latestResultLabelOverride,
  averagePriceChangeOverride,
  isDemo = false,
  headerAction,
}: WatchDogSelectionPanelCardProps) {
  const { language } = useLanguage();
  const shouldReduceMotion = useReducedMotion() ?? false;
  const copy = {
    cs: {
      watchdogs: "Moji hlídací psi",
      selectedWatchdog: "Vybraný hlídací pes",
      latestListing: "Poslední nalezený inzerát",
      latest: "Poslední",
      noLatestListing: "Zatím bez nalezeného inzerátu",
      noWatchdog: "Zatím nemáte hlídacího psa",
      selectWatchdog: "Vyberte hlídacího psa",
      noLocation: "Bez lokality",
      selected: "Vybráno",
      type: "Typ",
      offer: "Nabídka",
      status: "Stav",
      active: "Aktivní",
      paused: "Pozastaveno",
      addWatchdog: "Přidat hlídacího psa",
      newOffers: "Nové nabídky",
      updatedOffers: "Aktualizované nabídky",
      allOffers: "Všechny nabídky",
      tracking: "Sledování nabídek",
      results30Days: "Výsledky · 30 dní",
      latestResult: "Poslední výsledek",
      changePrevious30Days: "Změna ceny · 30 dní",
      averageOfferPrice: "Průměrná cena · 30 dní",
      today: "Dnes",
      noResults: "Bez výsledků",
      loading: "Načítám…",
      quickOverview: "Rychlý přehled",
      priceRange: "Cenové rozpětí",
      areaRange: "Užitná plocha",
      notifications: "Notifikace",
      notificationsOff: "Vypnuto",
      immediately: "Okamžitě",
      everyTwoHours: "Každé 2 hodiny",
      daily: "Denně",
      notSet: "Nenastaveno",
      unknown: "Nezjištěno",
      waitingForOffers: "Čekáme na první inzeráty",
    },
    en: {
      watchdogs: "My watchdogs",
      selectedWatchdog: "Selected watchdog",
      latestListing: "Latest found listing",
      latest: "Latest",
      noLatestListing: "No listing found yet",
      noWatchdog: "You do not have a watchdog yet",
      selectWatchdog: "Select a watchdog",
      noLocation: "No location",
      selected: "Selected",
      type: "Type",
      offer: "Offer",
      status: "Status",
      active: "Active",
      paused: "Paused",
      addWatchdog: "Add watchdog",
      newOffers: "New offers",
      updatedOffers: "Updated offers",
      allOffers: "All offers",
      tracking: "Offer tracking",
      results30Days: "Results · 30 days",
      latestResult: "Latest result",
      changePrevious30Days: "Price change · 30 days",
      averageOfferPrice: "Average price · 30 days",
      today: "Today",
      noResults: "No results",
      loading: "Loading…",
      quickOverview: "Quick overview",
      priceRange: "Price range",
      areaRange: "Usable area",
      notifications: "Notifications",
      notificationsOff: "Off",
      immediately: "Immediately",
      everyTwoHours: "Every 2 hours",
      daily: "Daily",
      notSet: "Not set",
      unknown: "Unknown",
      waitingForOffers: "Waiting for first listings",
    },
  }[language];
  const selectedWatchDog =
    watchdogs.find((watchDog) => watchDog.id === selectedWatchDogId) ?? null;
  const latestOffer = selectedWatchDog ? null : (latestOffers?.[0] ?? null);
  const summaryEstateType =
    selectedWatchDog?.estateType ??
    latestOffer?.watchdogEntity.estateType ??
    "";
  const SelectedIcon = getWatchDogIcon(summaryEstateType);
  const selectedLocation = selectedWatchDog
    ? normalizeDetailText(selectedWatchDog.searchAddress) ||
      normalizeDetailText(selectedWatchDog.exactAddress)
    : null;
  const locationLabel = selectedWatchDog
    ? getEstateValueLabel(selectedLocation, language) || copy.noLocation
    : latestOffer?.address ||
      latestOffer?.header ||
      (watchdogs.length === 0 ? copy.noWatchdog : copy.noLatestListing);
  const estateTypeLabel = selectedWatchDog
    ? getEstateValueLabel(selectedWatchDog.estateType, language)
    : latestOffer
      ? getEstateValueLabel(latestOffer.watchdogEntity.estateType, language)
      : "—";
  const offerTypeLabel = selectedWatchDog
    ? getEstateValueLabel(selectedWatchDog.offerType, language)
    : latestOffer
      ? getEstateValueLabel(latestOffer.watchdogEntity.offerType, language)
      : "—";
  const latestPrice = latestOffer ? parseOfferPrice(latestOffer.price) : null;
  const statusLabel = selectedWatchDog
    ? selectedWatchDog.isEnabled
      ? copy.active
      : copy.paused
    : latestOffer && latestPrice
      ? `${latestPrice.toLocaleString(language === "cs" ? "cs-CZ" : "en-US")} ${language === "cs" ? "Kč" : "CZK"}`
      : "—";
  const searchModeLabel = selectedWatchDog
    ? getSearchModeLabel(selectedWatchDog.sendOfferAs, {
        newOffers: copy.newOffers,
        updatedOffers: copy.updatedOffers,
        allOffers: copy.allOffers,
        fallback: copy.tracking,
      })
    : latestOffer?.header || copy.tracking;
  const offersForSummary = (summaryOffers ?? []).filter(
    (offer) =>
      selectedWatchDogId == null ||
      offer.watchdogEntity.id === selectedWatchDogId,
  );
  const currentPeriodOffers = offersForSummary.filter((offer) => {
    const ageInDays = getOfferAgeInDays(offer.createdAt);
    return ageInDays >= 0 && ageInDays < 30;
  });
  const previousPeriodOffers = offersForSummary.filter((offer) => {
    const ageInDays = getOfferAgeInDays(offer.createdAt);
    return ageInDays >= 30 && ageInDays < 60;
  });
  const newestSummaryOffer = offersForSummary.reduce<EstateOffer | null>(
    (newest, offer) =>
      newest == null ||
      new Date(offer.createdAt).getTime() > new Date(newest.createdAt).getTime()
        ? offer
        : newest,
    null,
  );
  const averagePrice = getAverageOfferPrice(currentPeriodOffers);
  const previousAveragePrice = getAverageOfferPrice(previousPeriodOffers);
  const averagePriceChange =
    averagePrice != null &&
    previousAveragePrice != null &&
    previousAveragePrice !== 0
      ? ((averagePrice - previousAveragePrice) / previousAveragePrice) * 100
      : null;
  const displayedAveragePriceChange =
    averagePriceChangeOverride ?? averagePriceChange;
  const averagePriceLabel = averagePrice
    ? `${averagePrice.toLocaleString(language === "cs" ? "cs-CZ" : "en-US")} ${language === "cs" ? "Kč" : "CZK"}`
    : copy.waitingForOffers;
  const averagePriceChangeLabel =
    displayedAveragePriceChange == null
      ? copy.unknown
      : `${displayedAveragePriceChange >= 0 ? "+" : ""}${displayedAveragePriceChange.toLocaleString(
          language === "cs" ? "cs-CZ" : "en-US",
          { maximumFractionDigits: 1 },
        )} %`;
  const latestResultLabel = newestSummaryOffer
    ? newestSummaryOffer.createdAt.slice(0, 10) === getLocalDateKey(new Date())
      ? copy.today
      : new Intl.DateTimeFormat(language === "cs" ? "cs-CZ" : "en-US", {
          day: "numeric",
          month: "short",
        }).format(new Date(newestSummaryOffer.createdAt))
    : copy.noResults;
  const hasSummary = summaryOffers != null;
  const locale = language === "cs" ? "cs-CZ" : "en-US";
  const selectedQuickMetrics = selectedWatchDog
    ? [
        {
          label: copy.priceRange,
          value:
            formatRange(
              selectedWatchDog.priceFrom,
              selectedWatchDog.priceTo,
              language === "cs" ? " Kč" : " CZK",
              locale,
            ) ?? copy.notSet,
        },
        {
          label: copy.areaRange,
          value:
            formatRange(
              selectedWatchDog.usableAreaFrom,
              selectedWatchDog.usableAreaTo,
              " m²",
              locale,
            ) ?? copy.notSet,
        },
        {
          label: copy.notifications,
          value: selectedWatchDog.doEmailReport
            ? {
                NONSTOP: copy.immediately,
                OFTEN: copy.everyTwoHours,
                DAILY: copy.daily,
              }[selectedWatchDog.emailReportFrequency] || copy.active
            : copy.notificationsOff,
        },
      ]
    : [];

  return (
    <div className="app-section-card public-summary-banner relative flex h-full w-full flex-col">
      {headerAction ? (
        <div className="absolute right-0 top-0 z-20">{headerAction}</div>
      ) : null}
      <div
        className={`flex items-center justify-between border-b border-softiq-primary/10 py-4 pl-5 dark:border-white/10 sm:pl-6 ${
          headerAction ? "pr-14" : "pr-5 sm:pr-6"
        }`}
      >
        <div>
          <div className="flex items-center gap-2">
            <p className="app-section-eyebrow public-banner-eyebrow">
              {copy.watchdogs}
            </p>
            {isDemo ? <DemoBrushBadge /> : null}
          </div>
          <p className="app-section-heading mt-1">{watchDogCountLabel}</p>
        </div>

        <div className="inline-flex shrink-0 flex-col items-center gap-1">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="unstyled"
                  onClick={onAddWatchDogClick}
                  loading={isMembershipLoading}
                  aria-label={copy.addWatchdog}
                  className="app-section-add-button group"
                >
                  <Image
                    aria-hidden="true"
                    src="/image/paw/paw-dark.svg"
                    alt={copy.addWatchdog}
                    width={20}
                    height={20}
                    className="size-5 brightness-0 invert dark:invert-0"
                  />
                  <span className="app-section-add-badge">
                    <PlusIcon
                      aria-hidden="true"
                      className="!size-2.5 stroke-[2.5] transition-transform group-hover:rotate-90"
                    />
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={15}>
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {isLoading ? (
        <div className="relative min-h-[240px] flex-1">
          <LoadingOverlayDots />
        </div>
      ) : (
        <div className="grid min-w-0 flex-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="relative min-h-[240px] min-w-0 max-w-full overflow-hidden border-b border-softiq-primary/10 dark:border-white/10 lg:border-b-0 lg:border-r">
            {!selectedWatchDog && latestContent ? (
              latestContent
            ) : (
              <div className="flex min-h-[240px] flex-col p-5 sm:p-6">
                <span className="app-section-description">
                  {selectedWatchDog
                    ? copy.averageOfferPrice
                    : copy.latestListing}
                </span>
                <motion.span
                  key={
                    isDemo
                      ? `${selectedWatchDogId ?? "latest"}-${averagePriceLabel}`
                      : "watchdog-price"
                  }
                  initial={
                    isDemo && !shouldReduceMotion
                      ? { opacity: 0, y: 8, scale: 0.94 }
                      : false
                  }
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={
                    isDemo && !shouldReduceMotion
                      ? {
                          type: "spring",
                          stiffness: 260,
                          damping: 18,
                          mass: 0.7,
                        }
                      : { duration: 0 }
                  }
                  className="mt-2 line-clamp-2 origin-left text-[2rem] font-medium leading-[1.05] tracking-[-0.05em]"
                >
                  {selectedWatchDog
                    ? isSummaryLoading
                      ? copy.loading
                      : averagePriceLabel
                    : locationLabel}
                </motion.span>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <StatusPill
                    appearance="flat"
                    tone={
                      selectedWatchDog?.isEnabled || latestOffer
                        ? "default"
                        : "warning"
                    }
                    className="px-2"
                  >
                    {statusLabel}
                  </StatusPill>
                  <span className="app-meta-muted">{searchModeLabel}</span>
                </div>

                <div className="mt-auto flex items-end justify-between pt-6">
                  <div className="app-meta-muted text-xs">
                    {[offerTypeLabel, estateTypeLabel]
                      .filter((label) => label !== "—")
                      .join(" · ") || "—"}
                  </div>
                  <span className="flex size-14 items-center justify-center rounded-2xl bg-softiq-secondary-shade-200 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary">
                    <SelectedIcon aria-hidden="true" className="size-7" />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div
            className={`relative z-10 min-w-0 p-3 sm:p-4 ${
              watchdogs.length > 0 || !emptyContent
                ? "max-h-[240px] overflow-y-auto"
                : "overflow-visible"
            }`}
          >
            {watchdogs.length > 0 ? (
              <div className="space-y-1">
                {watchdogs.map((watchDog) => (
                  <WatchDogSelectorButton
                    key={watchDog.id}
                    watchDog={watchDog}
                    isActive={selectedWatchDogId === watchDog.id}
                    onClick={() => onSelectWatchDog(watchDog)}
                  />
                ))}
              </div>
            ) : emptyContent ? (
              emptyContent
            ) : (
              <button
                type="button"
                onClick={onAddWatchDogClick}
                className="app-section-empty"
              >
                {copy.addWatchdog}
              </button>
            )}
          </div>
        </div>
      )}

      {!isLoading && hasSummary ? (
        <SummaryStatGrid
          stats={[
            {
              label: copy.results30Days,
              value: isSummaryLoading
                ? copy.loading
                : (summaryResultCountOverride ?? currentPeriodOffers.length),
            },
            {
              label: copy.latestResult,
              value: isSummaryLoading
                ? copy.loading
                : (latestResultLabelOverride ?? latestResultLabel),
            },
            {
              label: copy.changePrevious30Days,
              value: isSummaryLoading ? copy.loading : averagePriceChangeLabel,
              tone:
                displayedAveragePriceChange == null
                  ? ("default" as const)
                  : displayedAveragePriceChange >= 0
                    ? ("positive" as const)
                    : ("negative" as const),
            },
          ]}
        />
      ) : !isLoading ? (
        <SummaryStatGrid
          stats={[
            {
              label: selectedWatchDog ? copy.selected : copy.latest,
              value: locationLabel,
            },
            { label: copy.type, value: estateTypeLabel },
            { label: copy.offer, value: offerTypeLabel },
          ]}
        />
      ) : null}

      {!isLoading && selectedWatchDog && showSelectedDetails ? (
        <HomeSelectionQuickDetails
          icon={<SelectedIcon aria-hidden="true" className="size-5" />}
          eyebrow={copy.quickOverview}
          title={locationLabel}
          badge={
            <StatusPill
              appearance="flat"
              tone={selectedWatchDog.isEnabled ? "success" : "warning"}
            >
              {statusLabel}
            </StatusPill>
          }
          subtitle={
            [offerTypeLabel, estateTypeLabel]
              .filter(
                (value) => normalizeDetailText(value) != null && value !== "—",
              )
              .join(" · ") || copy.notSet
          }
          metrics={selectedQuickMetrics}
        />
      ) : null}

      {footer ? (
        <div className="app-section-footer mt-auto">{footer}</div>
      ) : null}
    </div>
  );
}
